import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, BadgePercent, CheckCircle2, Minus, Plus, Trash2, X } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, errMsg, fileUrl, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/Reveal";

export default function Cart() {
  const { items, setQty, remove, clear, subtotal, savings } = useCart();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [form, setForm] = useState({ customer_name: "", phone: "", address: "", city: "", zip: "", payment_method: "paypal", use_points: false });

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && !form.customer_name) setForm((f) => ({ ...f, customer_name: user.name || "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Si le moyen de paiement choisi est désactivé dans l'admin, bascule sur le premier actif
  useEffect(() => {
    if (!settings) return;
    const pm = settings.payment_methods || { paypal: true, virement: true };
    const first = ["paypal", "virement"].find((v) => pm[v] !== false);
    if (first && pm[form.payment_method] === false) setForm((f) => ({ ...f, payment_method: first }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    try {
      const { data } = await api.post("/promos/validate", {
        code: promoInput.trim(),
        email: user?.email || "",
        items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
      });
      setPromo(data);
      toast.success(
        data.type === "fixed" ? `Code ${data.code} appliqué : -${fmt(data.discount)}` : `Code ${data.code} appliqué : -${data.value} %`
      );
    } catch (err) {
      setPromo(null);
      toast.error(errMsg(err));
    } finally {
      setPromoLoading(false);
    }
  };

  if (order) {
    return (
      <div className="max-w-3xl mx-auto px-5 pt-16 pb-10" data-testid="order-confirmation">
        <Reveal className="text-center">
          <span className="inline-flex w-16 h-16 rounded-full bg-kay-rose items-center justify-center mb-6">
            <CheckCircle2 className="text-kay-green" size={30} />
          </span>
          <h1 className="font-display text-4xl sm:text-5xl">Commande <span className="italic text-kay-pink">reçue !</span></h1>
          <p className="mt-4 text-kay-ink/60 text-sm" data-testid="order-number">Numéro de commande : <span className="font-mono font-bold">{order.number}</span></p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10 rounded-3xl border border-kay-line bg-white p-7 lg:p-10 grid gap-5" data-testid="payment-instructions">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-kay-ink/50">Dernière étape : le paiement</p>
            {order.payment_method === "paypal" ? (
              <p className="text-sm leading-relaxed text-kay-ink/70">
                Envoie <strong>{fmt(order.total)}</strong> par PayPal{settings?.paypal_email ? <> à <strong className="font-mono">{settings.paypal_email}</strong></> : <> (l'adresse PayPal te sera confirmée sur Snapchat)</>} en indiquant ton numéro de commande <strong className="font-mono">{order.number}</strong>.
              </p>
            ) : (
              <p className="text-sm leading-relaxed text-kay-ink/70">
                Effectue un virement de <strong>{fmt(order.total)}</strong>{settings?.iban ? <> vers l'IBAN <strong className="font-mono">{settings.iban}</strong></> : <> (les coordonnées bancaires te seront communiquées sur Snapchat)</>} en indiquant <strong className="font-mono">{order.number}</strong> en référence.
              </p>
            )}
            <div className="rounded-2xl bg-kay-rose/50 border border-kay-line px-5 py-4 text-sm">
              Une question ? Écris-moi sur Snapchat : <strong className="font-mono">{settings?.snapchat || ""}</strong> — ta commande est expédiée dès réception du paiement.
            </div>
            <div className="grid gap-2 border-t border-kay-line pt-4" data-testid="order-items-recap">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-kay-ink/50">Ta commande</p>
              {order.items.map((it, idx) => (
                <div key={idx} className="flex justify-between gap-3 text-sm" data-testid={`order-item-${idx}`}>
                  <span className="text-kay-ink/70">
                    {it.qty}× {it.name}
                    {it.variant && <span className="text-kay-pink"> — {it.variant}</span>}
                    {it.variant_ref && <span className="font-mono text-[10px] text-kay-ink/40"> (réf. {it.variant_ref})</span>}
                  </span>
                  <span className="font-semibold whitespace-nowrap">{fmt(it.price * it.qty)}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between border-t border-kay-line pt-4 text-sm">
              <span>Total à payer</span>
              <span className="font-display text-2xl font-semibold" data-testid="order-total">{fmt(order.total)}</span>
            </div>
            {order.points_earned > 0 && (
              <p className="font-mono text-xs text-kay-green font-semibold" data-testid="points-earned">
                + {order.points_earned} points de fidélité gagnés
              </p>
            )}
            <p className="text-[11px] text-kay-ink/40">Un email de confirmation vient de partir vers ta boîte mail.</p>
            <Link to="/boutique" className="rounded-full bg-kay-ink text-white text-center py-3.5 text-sm font-semibold hover:bg-kay-pink transition-colors">
              Continuer mes achats
            </Link>
          </div>
        </Reveal>
      </div>
    );
  }

  const shipping = settings ? (subtotal >= settings.free_shipping_above || items.length === 0 ? 0 : settings.shipping_fee) : 0;
  const paymentMethods = [
    { v: "paypal", label: "PayPal" },
    { v: "virement", label: "Virement bancaire" },
  ].filter((m) => (settings?.payment_methods || { paypal: true, virement: true })[m.v] !== false);
  const blocks = settings && user ? Math.min(Math.floor((user.loyalty_points || 0) / settings.points_block), Math.floor(subtotal / settings.block_value)) : 0;
  const pointsDiscount = form.use_points ? blocks * settings.block_value : 0;
  const promoDiscount = promo ? promo.discount : 0;
  const total = Math.max(0, subtotal - pointsDiscount - promoDiscount) + shipping;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        ...form,
        use_points: form.use_points && blocks > 0,
        promo_code: promo?.code || null,
        items: items.map((i) => ({ product_id: i.id, qty: i.qty, variant: i.variant })),
      });
      if (data.paypal_approval_url) {
        clear();
        toast.success("Commande enregistrée — redirection vers PayPal…");
        window.location.href = data.paypal_approval_url;
        return;
      }
      setOrder(data);
      clear();
      refresh();
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-10" data-testid="cart-page">
      <Reveal>
        <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-3">Ton panier</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl">
          Presque <span className="italic text-kay-pink">à toi.</span>
        </h1>
      </Reveal>

      {items.length === 0 ? (
        <div className="py-24 text-center" data-testid="cart-empty">
          <p className="font-display italic text-3xl text-kay-ink/50">Ton panier est encore vide…</p>
          <Link to="/boutique" data-testid="cart-shop-link" className="mt-8 inline-flex items-center gap-2 rounded-full bg-kay-ink text-white px-8 py-4 text-sm font-semibold hover:bg-kay-pink transition-colors">
            Découvrir les offres <ArrowRight size={15} />
          </Link>
        </div>
      ) : (
        <div className="mt-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 grid gap-4">
            {items.map((i) => (
              <div key={i.key} className="flex gap-4 rounded-2xl border border-kay-line bg-white p-4 items-center" data-testid={`cart-item-${i.key}`}>
                <div className="w-20 h-24 rounded-xl bg-kay-rose/40 border border-kay-line overflow-hidden shrink-0 flex items-center justify-center">
                  {i.image ? (
                    <img src={fileUrl(i.image)} alt={i.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="font-display italic text-xs text-kay-ink/40 px-2 text-center">Photo à venir</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-kay-pink">{i.brand}</p>
                  <p className="font-display text-lg leading-tight truncate">{i.name}</p>
                  {i.variant && (
                    <p className="font-mono text-[10px] uppercase tracking-widest text-kay-ink/50 mt-0.5" data-testid={`cart-variant-${i.key}`}>
                      Coloris : {i.variant}
                    </p>
                  )}
                  <p className={`font-mono text-[10px] mt-0.5 ${i.qty > i.stock ? "text-red-500 font-bold" : i.stock <= 3 ? "text-kay-pink" : "text-kay-ink/40"}`} data-testid={`cart-stock-${i.key}`}>
                    {i.qty > i.stock ? `Stock insuffisant — plus que ${i.stock} dispo` : i.stock <= 3 ? `Plus que ${i.stock} en stock` : `${i.stock} en stock`}
                  </p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="font-semibold text-sm">{fmt(i.price)}</span>
                    {i.reference_price > i.price && (
                      <span className="font-mono text-[11px] text-kay-ink/40 line-through">{fmt(i.reference_price)}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`qty-minus-${i.key}`} onClick={() => setQty(i.key, i.qty - 1)} className="w-8 h-8 rounded-full border border-kay-line flex items-center justify-center hover:border-kay-ink transition-colors" aria-label="Moins">
                    <Minus size={13} />
                  </button>
                  <span className="w-6 text-center text-sm font-semibold" data-testid={`qty-${i.key}`}>{i.qty}</span>
                  <button data-testid={`qty-plus-${i.key}`} onClick={() => setQty(i.key, Math.min(i.qty + 1, i.stock))} className="w-8 h-8 rounded-full border border-kay-line flex items-center justify-center hover:border-kay-ink transition-colors" aria-label="Plus">
                    <Plus size={13} />
                  </button>
                </div>
                <button data-testid={`remove-${i.key}`} onClick={() => remove(i.key)} className="p-2 text-kay-ink/40 hover:text-kay-pink transition-colors" aria-label="Retirer">
                  <Trash2 size={17} />
                </button>
              </div>
            ))}
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-kay-line bg-white p-7 lg:p-8 sticky top-24" data-testid="cart-summary">
              <h2 className="font-display text-2xl mb-6">Résumé</h2>

              <div className="mb-5" data-testid="promo-section">
                {promo ? (
                  <div className="flex items-center justify-between rounded-xl border border-kay-green/30 bg-kay-green/5 px-4 py-3">
                    <span className="font-mono text-xs font-bold text-kay-green flex items-center gap-2">
                      <BadgePercent size={14} /> {promo.code} · {promo.type === "fixed" ? `-${fmt(promo.discount)}` : `-${promo.value} %`}
                    </span>
                    <button onClick={() => setPromo(null)} className="text-kay-ink/40 hover:text-kay-pink" aria-label="Retirer le code" data-testid="promo-remove">
                      <X size={15} />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      data-testid="promo-input"
                      value={promoInput}
                      onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                      placeholder="Code promo"
                      className="flex-1 rounded-xl border border-kay-line px-4 py-2.5 text-sm font-mono uppercase outline-none focus:border-kay-pink"
                    />
                    <button onClick={applyPromo} disabled={promoLoading} data-testid="promo-apply" className="rounded-full border border-kay-ink px-5 text-xs font-bold hover:bg-kay-ink hover:text-white transition-colors disabled:opacity-50">
                      {promoLoading ? "…" : "Appliquer"}
                    </button>
                  </div>
                )}
              </div>

              <div className="grid gap-3 text-sm">
                <div className="flex justify-between"><span className="text-kay-ink/60">Sous-total</span><span data-testid="cart-subtotal" className="font-semibold">{fmt(subtotal)}</span></div>
                {savings > 0 && (
                  <div className="flex justify-between text-kay-green">
                    <span>Réductions boutique</span>
                    <span data-testid="cart-savings" className="font-mono font-semibold">-{fmt(savings)}</span>
                  </div>
                )}
                {promoDiscount > 0 && (
                  <div className="flex justify-between text-kay-green">
                    <span>Code promo {promo.code}</span>
                    <span data-testid="cart-promo-discount" className="font-mono font-semibold">-{fmt(promoDiscount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-kay-ink/60">Livraison</span>
                  <span data-testid="cart-shipping" className="font-semibold">{shipping === 0 ? "Offerte" : fmt(shipping)}</span>
                </div>
                {form.use_points && pointsDiscount > 0 && (
                  <div className="flex justify-between text-kay-green">
                    <span>Points fidélité utilisés</span>
                    <span className="font-mono font-semibold">-{fmt(pointsDiscount)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-kay-line pt-4 items-baseline">
                  <span className="font-semibold">Total</span>
                  <span className="font-display text-3xl font-semibold" data-testid="cart-total">{fmt(total)}</span>
                </div>
              </div>
              {savings > 0 && (
                <p className="mt-4 rounded-xl bg-kay-rose/50 border border-kay-line px-4 py-3 font-mono text-xs font-semibold text-kay-wine" data-testid="cart-savings-message">
                  Tu économises {fmt(savings + promoDiscount)} grâce à nos prix réduits{promo ? " et ton code promo" : ""}.
                </p>
              )}
              {settings && items.length > 0 && (
                <div className="mt-4" data-testid="free-shipping-progress">
                  {subtotal < settings.free_shipping_above ? (
                    <>
                      <p className="text-xs text-kay-ink/60 mb-2">
                        Plus que <strong className="text-kay-pink">{fmt(settings.free_shipping_above - subtotal)}</strong> pour profiter de la livraison offerte ♡
                      </p>
                      <div className="h-2 rounded-full bg-kay-rose/50 overflow-hidden">
                        <motion.div
                          className="h-full rounded-full bg-gradient-to-r from-kay-rosedeep to-kay-pink"
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(100, (subtotal / settings.free_shipping_above) * 100)}%` }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                          data-testid="free-shipping-bar"
                        />
                      </div>
                    </>
                  ) : (
                    <p className="text-xs font-bold text-kay-green flex items-center gap-1.5" data-testid="free-shipping-unlocked">
                      <CheckCircle2 size={14} /> Livraison offerte débloquée ♡
                    </p>
                  )}
                </div>
              )}

              {!user ? (
                <div className="mt-6 rounded-2xl border border-kay-line bg-kay-cream p-5 text-center">
                  <p className="text-sm mb-4">Crée ton compte (ou connecte-toi) pour valider ta commande et cumuler des points de fidélité.</p>
                  <button data-testid="checkout-login-button" onClick={() => navigate("/compte")} className="w-full rounded-full bg-kay-ink text-white py-3.5 text-sm font-semibold hover:bg-kay-pink transition-colors">
                    Me connecter pour commander
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-6 grid gap-3" data-testid="checkout-form">
                  {blocks > 0 && (
                    <label className="flex items-center gap-3 rounded-xl border border-kay-line bg-kay-cream px-4 py-3 text-sm cursor-pointer">
                      <input type="checkbox" data-testid="use-points-checkbox" checked={form.use_points} onChange={(e) => setForm({ ...form, use_points: e.target.checked })} className="accent-[#E05297] w-4 h-4" />
                      Utiliser mes points : -{fmt(blocks * settings.block_value)} ({user.loyalty_points} pts)
                    </label>
                  )}
                  <input required data-testid="checkout-name" placeholder="Nom complet" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="rounded-xl border border-kay-line px-4 py-3 text-sm outline-none focus:border-kay-pink" />
                  <input data-testid="checkout-phone" placeholder="Téléphone (facultatif)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl border border-kay-line px-4 py-3 text-sm outline-none focus:border-kay-pink" />
                  <input required data-testid="checkout-address" placeholder="Adresse" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-xl border border-kay-line px-4 py-3 text-sm outline-none focus:border-kay-pink" />
                  <div className="grid grid-cols-2 gap-3">
                    <input required data-testid="checkout-zip" placeholder="Code postal" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="rounded-xl border border-kay-line px-4 py-3 text-sm outline-none focus:border-kay-pink" />
                    <input required data-testid="checkout-city" placeholder="Ville" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-xl border border-kay-line px-4 py-3 text-sm outline-none focus:border-kay-pink" />
                  </div>
                  <div className="grid grid-cols-2 gap-3" data-testid="payment-method-group">
                    {paymentMethods.map((m) => (
                      <button
                        type="button"
                        key={m.v}
                        data-testid={`payment-${m.v}`}
                        onClick={() => setForm({ ...form, payment_method: m.v })}
                        className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-all ${
                          form.payment_method === m.v ? "border-kay-ink bg-kay-ink text-white" : "border-kay-line hover:border-kay-ink"
                        }`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                  <button
                    data-testid="place-order-button"
                    type="submit"
                    disabled={loading}
                    className="rounded-full bg-kay-ink text-white py-4 text-sm font-bold hover:bg-kay-pink transition-colors disabled:opacity-50"
                  >
                    {loading ? "Validation…" : `Passer commande — ${fmt(total)}`}
                  </button>
                  <p className="text-[11px] text-kay-ink/40 text-center">
                    PayPal : paiement en ligne sécurisé, immédiat. Virement : coordonnées transmises après validation.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
